import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { DividendRecord, SplitRecord } from '../../types/tickerDetail';
import { useChartSize } from './useChartSize';

/**
 * Dividend per share over time, with splits marked.
 *
 * The question this answers is whether the payout is growing, flat or cut —
 * so magnitude gets the y-axis and splits become annotations rather than a
 * series of their own. A split has no magnitude worth plotting (a 4:1 is not
 * "bigger" than a 2:1), but it does explain a step change in the bars, which
 * is exactly what an annotation is for.
 */

const COLORS = {
  grid: '#2B2B2B', // dark-border
  axis: '#C7C2B8', // text-muted
  bar: '#1C76A2', // bull-blue
  barCut: '#D8483B', // bear-red — a payment below the one before it
  split: '#C2A14E', // accent-yellow
} as const;

const HEIGHT = 260;
const MARGIN = { top: 14, right: 16, bottom: 26, left: 52 };
const MIN_BAR_W = 2;
const MAX_BAR_W = 26;

interface Point {
  date: Date;
  iso: string;
  amount: number;
  paymentDate: string;
  /** Smaller than the previous payment — a cut, worth colouring. */
  isCut: boolean;
}

interface SplitPoint {
  date: Date;
  iso: string;
  label: string;
}

/**
 * Dividend records into plottable points, oldest first.
 *
 * Uses `dividend`, deliberately not `adjDividend`. Adjusted figures are
 * retroactively rescaled for later splits, so a pre-split payment shows as a
 * fraction of what shareholders actually received and the series manufactures
 * a growth curve that never happened. The raw figure with a visible split
 * marker tells the truth: the step is real, and the marker explains it.
 */
function toPoints(records: DividendRecord[]): Point[] {
  const points = records
    .filter((r) => r?.date && Number.isFinite(r.dividend) && r.dividend > 0)
    .map((r) => ({
      date: new Date(`${r.date}T00:00:00`),
      iso: r.date,
      amount: r.dividend,
      paymentDate: r.paymentDate || '',
      isCut: false,
    }))
    .filter((p) => !isNaN(p.date.getTime()))
    .sort((a, b) => a.iso.localeCompare(b.iso));

  // Flagged only after sorting — the feed arrives newest-first, so comparing
  // against the previous element beforehand would invert every comparison and
  // paint every raise as a cut.
  for (let i = 1; i < points.length; i++) {
    points[i].isCut = points[i].amount < points[i - 1].amount;
  }
  return points;
}

function toSplits(records: SplitRecord[]): SplitPoint[] {
  return records
    .filter((s) => s?.date && s.numerator > 0 && s.denominator > 0)
    .map((s) => ({
      date: new Date(`${s.date}T00:00:00`),
      iso: s.date,
      label: s.label?.trim() || `${s.numerator}:${s.denominator}`,
    }))
    .filter((s) => !isNaN(s.date.getTime()))
    .sort((a, b) => a.iso.localeCompare(b.iso));
}

interface Props {
  dividends: DividendRecord[];
  splits: SplitRecord[];
}

type Hover =
  | { kind: 'dividend'; x: number; point: Point }
  | { kind: 'split'; x: number; point: SplitPoint };

export function DividendHistoryChart({ dividends, splits }: Props): React.ReactElement | null {
  const { ref: containerRef, width } = useChartSize<HTMLDivElement>();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  const points = useMemo(() => toPoints(dividends), [dividends]);
  // Splits outside the dividend window would stretch the time axis into empty
  // space, so only those alongside a payment are drawn.
  const splitPoints = useMemo(() => {
    const all = toSplits(splits);
    if (!points.length) return all;
    const first = points[0].iso;
    const last = points[points.length - 1].iso;
    return all.filter((s) => s.iso >= first && s.iso <= last);
  }, [splits, points]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (!width || !points.length) return;

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;
    if (innerW <= 0) return;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // A time scale, not a band scale. Ex-dates are irregular, and evenly
    // spacing them would hide a company moving from quarterly to annual
    // payments — the gap is part of the story.
    const extent = d3.extent(points, (p) => p.date) as [Date, Date];
    // A single payment has no extent to scale; give it a nominal year either
    // side so the bar lands mid-chart instead of on a zero-width domain.
    const domain: [Date, Date] =
      extent[0].getTime() === extent[1].getTime()
        ? [d3.timeYear.offset(extent[0], -1), d3.timeYear.offset(extent[1], 1)]
        : extent;

    const x = d3.scaleTime().domain(domain).range([0, innerW]).nice();
    const maxAmount = d3.max(points, (p) => p.amount) ?? 0;
    const y = d3.scaleLinear().domain([0, maxAmount]).nice().range([innerH, 0]);

    // Bar width from the tightest gap between payments, so bars never overlap
    // however irregular the schedule is.
    let barW = MAX_BAR_W;
    for (let i = 1; i < points.length; i++) {
      const gap = x(points[i].date) - x(points[i - 1].date);
      if (gap > 0 && gap * 0.7 < barW) barW = gap * 0.7;
    }
    barW = Math.max(MIN_BAR_W, Math.min(MAX_BAR_W, barW));

    g.append('g')
      .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(() => ''))
      .call((sel) => sel.select('.domain').remove())
      .selectAll('line')
      .attr('stroke', COLORS.grid);

    g.append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(4)
          .tickFormat((d) => `$${(d as number).toFixed(2)}`)
      )
      .call((sel) => sel.select('.domain').remove())
      .call((sel) => sel.selectAll('line').remove())
      .selectAll('text')
      .attr('fill', COLORS.axis)
      .attr('font-size', 10);

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(Math.max(2, Math.floor(innerW / 90))))
      .call((sel) => sel.select('.domain').attr('stroke', COLORS.grid))
      .call((sel) => sel.selectAll('line').remove())
      .selectAll('text')
      .attr('fill', COLORS.axis)
      .attr('font-size', 10);

    // Splits behind the bars — context, not the subject.
    for (const s of splitPoints) {
      const sx = x(s.date);
      g.append('line')
        .attr('x1', sx)
        .attr('x2', sx)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', COLORS.split)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3');

      g.append('text')
        .attr('x', sx)
        .attr('y', -4)
        .attr('text-anchor', 'middle')
        .attr('fill', COLORS.split)
        .attr('font-size', 10)
        .text(s.label);

      g.append('rect')
        .attr('x', sx - 6)
        .attr('y', 0)
        .attr('width', 12)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseenter', () => setHover({ kind: 'split', x: sx + MARGIN.left, point: s }))
        .on('mouseleave', () => setHover(null));
    }

    for (const p of points) {
      const px = x(p.date) - barW / 2;
      const py = y(p.amount);
      g.append('rect')
        .attr('x', px)
        .attr('y', py)
        .attr('width', barW)
        .attr('height', Math.max(1, innerH - py))
        .attr('fill', p.isCut ? COLORS.barCut : COLORS.bar);

      // Hit area widened to at least 8px — quarterly bars over a decade get
      // thin enough to be hard to hover accurately.
      const hitW = Math.max(barW, 8);
      g.append('rect')
        .attr('x', x(p.date) - hitW / 2)
        .attr('y', 0)
        .attr('width', hitW)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseenter', () =>
          setHover({ kind: 'dividend', x: x(p.date) + MARGIN.left, point: p })
        )
        .on('mouseleave', () => setHover(null));
    }
  }, [points, splitPoints, width]);

  if (!points.length) return null;

  const total = points.length;
  const latest = points[total - 1];

  return (
    <div ref={containerRef} className="relative w-full">
      <svg ref={svgRef} width={width} height={HEIGHT} />

      {hover && (
        <div
          className="pointer-events-none absolute z-10 rounded border border-dark-border bg-dark-surface px-2.5 py-2 text-xs shadow-lg"
          style={{
            left: Math.min(Math.max(hover.x - 70, 0), Math.max(width - 150, 0)),
            top: MARGIN.top,
          }}
        >
          {hover.kind === 'dividend' ? (
            <>
              <div className="text-white font-semibold font-numeric">
                ${hover.point.amount.toFixed(4)}
              </div>
              <div className="text-gray-400 whitespace-nowrap">Ex-date {hover.point.iso}</div>
              {hover.point.paymentDate && (
                <div className="text-gray-400 whitespace-nowrap">
                  Paid {hover.point.paymentDate}
                </div>
              )}
              {hover.point.isCut && <div className="text-bear-red mt-0.5">Lower than previous</div>}
            </>
          ) : (
            <>
              <div className="text-white font-semibold font-numeric">
                {hover.point.label} split
              </div>
              <div className="text-gray-400 whitespace-nowrap">{hover.point.iso}</div>
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[11px] text-gray-400">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ background: COLORS.bar }}
          />
          Dividend / share
        </span>
        {points.some((p) => p.isCut) && (
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ background: COLORS.barCut }}
            />
            Lower than previous
          </span>
        )}
        {splitPoints.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 border-t border-dashed"
              style={{ borderColor: COLORS.split }}
            />
            Split
          </span>
        )}
        <span className="ml-auto font-numeric">
          {total} payment{total === 1 ? '' : 's'} · latest ${latest.amount.toFixed(4)}
        </span>
      </div>
    </div>
  );
}
